/* eslint-disable max-lines -- Pre-existing violation */
/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {
  Card,
  DataStatus,
  Dialog,
  Form,
  FormButton,
  FormField,
  type FormControl,
  type FormSubmitHandler,
  Separator,
  Table,
  TableRow,
  closeCurrentDialog,
  openConfirmDialog,
  openDialog,
} from 'platform/components';
import {Grid, HStack, Show, Text, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useRef, useState, type MutableRefObject} from 'react';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  newVehicleApi,
  useArchiveColourMutation,
  useArchiveUpholsteryMutation,
  useCreateColourMutation,
  useCreateUpholsteryMutation,
  useGetColourUpholsteryRestrictionsQuery,
  useListColourQuery,
  useListTrimQuery,
  useListUpholsteryQuery,
  usePutColourTrimsMutation,
  usePutColourUpholsteryRestrictionsMutation,
  usePutTrimUpholsteryMutation,
  useUpdateColourMutation,
  useUpdateUpholsteryMutation,
  type ColourResponseBody,
  type TrimResponseBody,
  type UpholsteryResponseBody,
} from '@dms/api/new-vehicle';
import {MoneyText, useNewVehicleCatalogueLabels} from '@dms/features/new-vehicle';
import {useVehicleCatalogue} from '@dms/shared';

import type {ApiError, Nullish} from 'shared/web';

import {
  actionsColumn,
  archiveRowAction,
  Cell,
  ColourSwatch,
  column,
  editRowAction,
  FormSection,
  handleCatalogueError,
  isStaleCatalogueVersion,
  joinParts,
  OfferedOn,
  hasAnySwatch,
  PASSENGER_CAR,
  RowActions,
  SwatchLabel,
  TabIntro,
  TOGGLE_COLUMN_WIDTH,
  ToggleTile,
  t,
} from './catalogueUi';
import {colourSchema} from './colourSchema';
import {GrossPriceField} from './GrossPriceField';
import {NewPricelistVersionDialog} from './NewPricelistVersionDialog';
import {NumberField} from './NumberField';
import {PriceRow} from './PriceRow';
import {PriceWithGross} from './PriceWithGross';
import {upholsterySchema} from './upholsterySchema';
import {useCatalogueVatRate} from './useCatalogueVatRate';
import {useMatrixSaveQueue} from './useMatrixSaveQueue';
import {usePublishedPriceSave} from './usePublishedPriceSave';

const replaceFactoryCode = (
  codes: string[],
  previous: string | null | undefined,
  next: string
): string[] => {
  if ((previous ?? '') === next) {
    return codes;
  }
  if (previous && codes.includes(previous)) {
    return next
      ? codes.map((code) => (code === previous ? next : code))
      : codes.filter((code) => code !== previous);
  }
  return next ? [...codes, next] : codes;
};
type ColourValues = z.infer<typeof colourSchema>;
type ColourScalarValues = Omit<ColourValues, 'trimIds'>;
type PersistedColour = {
  id: string;
  version: number;
  codes: string[];
  values: ColourScalarValues;
};
type UpholsteryValues = z.infer<typeof upholsterySchema>;
const EMPTY_UPHOLSTERY: UpholsteryValues = {
  name: '',
  fabric: '',
  factoryCode: '',
  hex: '',
  priceNet: '0.00',
  costNet: '',
};

/**
 * After a version conflict, adopt the colour's current version (and codes) so a retry does not
 * resend the stale one. The saved scalar values stay, so a retry still skips an update it already
 * made and only repeats the trim assignment against the fresh version.
 */
function useRefreshPersistedColourVersion() {
  const [getColour] = newVehicleApi.useLazyGetColourQuery();
  return async (persisted: MutableRefObject<PersistedColour | null>) => {
    const known = persisted.current;
    if (!known) {
      return;
    }
    const current = await getColour({colourId: known.id}, false)
      .unwrap()
      .catch(() => null);
    if (current && persisted.current?.id === known.id) {
      persisted.current = {...known, version: current.version, codes: current.codes};
    }
  };
}

const getColourScalarValues = (values: ColourValues): ColourScalarValues => ({
  name: values.name,
  kind: values.kind,
  factoryCode: values.factoryCode,
  hex: values.hex,
  priceNet: values.priceNet,
  costNet: values.costNet,
});

type ColourFieldsProps = {
  control: FormControl<ColourValues>;
  modelYearId: string;
  trims: TrimResponseBody[];
  canViewCost: boolean;
  isCreate?: boolean;
  submitTitle: string;
};

function ColourFields(props: ColourFieldsProps) {
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate(props.modelYearId);
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          placeholder="Frozen White"
          isRequired={props.isCreate}
        />
        <Grid columns={3} spacing={3}>
          <FormField
            control={props.control}
            name="kind"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.kind', 'Kind')}
            options={catalogue.bodyColorTypeOptions ?? []}
            isLoading={isLoading}
            isDisabled={isNilOrEmpty(catalogue.bodyColorTypeOptions)}
          />
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
          <FormField
            control={props.control}
            name="hex"
            type="text"
            label={t('entity.newVehicle.catalogue.hex', 'Hex')}
            placeholder="#1A2B3C"
            helperText={t(
              'entity.newVehicle.catalogue.hexHelp',
              'Used for the swatch salespeople see.'
            )}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.price', 'Price')}>
        <PriceRow vatRate={vatRate} canViewCost={props.canViewCost} spacing={3}>
          <GrossPriceField
            control={props.control}
            name="priceNet"
            vatRate={vatRate}
            label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
            helperText={t(
              'entity.newVehicle.catalogue.surchargeHelp',
              'Surcharge on top of the vehicle. Use 0 for a no-cost option.'
            )}
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.availability', 'Availability')}>
        <FormField
          control={props.control}
          name="trimIds"
          type="multiChoice"
          menuInPortal
          label={t('entity.newVehicle.catalogue.offeredOn', 'Offered on')}
          helperText={t(
            'entity.newVehicle.catalogue.offeredOnHelp',
            'Customers can only pick this colour on the trims you select here.'
          )}
          options={props.trims.map(({id, name}) => ({value: id, label: name}))}
        />
      </FormSection>
      <HStack justify="flex-end">
        <FormButton control={props.control} type="submit" title={props.submitTitle} />
      </HStack>
    </VStack>
  );
}

type UpholsteryFieldsProps = {
  control: FormControl<UpholsteryValues>;
  modelYearId: string;
  canViewCost: boolean;
  isCreate?: boolean;
  submitTitle: string;
};

function UpholsteryFields(props: UpholsteryFieldsProps) {
  const [catalogue, {isLoading}] = useVehicleCatalogue(PASSENGER_CAR);
  const vatRate = useCatalogueVatRate(props.modelYearId);
  return (
    <VStack spacing={4}>
      <FormSection title={t('entity.newVehicle.catalogue.section.identity', 'Identity')}>
        <FormField
          control={props.control}
          name="name"
          type="text"
          label={t('general.labels.name', 'Name')}
          placeholder="Ebony Cloth"
          isRequired={props.isCreate}
        />
        <Grid columns={3} spacing={3}>
          <FormField
            control={props.control}
            name="fabric"
            type="choice"
            menuInPortal
            label={t('entity.newVehicle.catalogue.fabric', 'Fabric')}
            options={catalogue.interiorMaterialOptions ?? []}
            isLoading={isLoading}
            isDisabled={isNilOrEmpty(catalogue.interiorMaterialOptions)}
          />
          <FormField
            control={props.control}
            name="factoryCode"
            type="text"
            label={t('entity.newVehicle.catalogue.factoryCode', 'Factory code')}
          />
          <FormField
            control={props.control}
            name="hex"
            type="text"
            label={t('entity.newVehicle.catalogue.hex', 'Hex')}
            placeholder="#1A2B3C"
            helperText={t(
              'entity.newVehicle.catalogue.hexHelp',
              'Used for the swatch salespeople see.'
            )}
          />
        </Grid>
      </FormSection>
      <Separator spacing={0} />
      <FormSection title={t('entity.newVehicle.catalogue.section.price', 'Price')}>
        <PriceRow vatRate={vatRate} canViewCost={props.canViewCost} spacing={3}>
          <GrossPriceField
            control={props.control}
            name="priceNet"
            vatRate={vatRate}
            label={t('entity.newVehicle.catalogue.priceNet', 'Price net')}
            helperText={t(
              'entity.newVehicle.catalogue.surchargeHelp',
              'Surcharge on top of the vehicle. Use 0 for a no-cost option.'
            )}
          />
          <Show when={props.canViewCost}>
            <NumberField
              control={props.control}
              name="costNet"
              label={t('entity.newVehicle.catalogue.costNet', 'Cost net')}
            />
          </Show>
        </PriceRow>
      </FormSection>
      <HStack justify="flex-end">
        <FormButton control={props.control} type="submit" title={props.submitTitle} />
      </HStack>
    </VStack>
  );
}

function SwatchName(props: {
  hex: string | Nullish;
  showSwatch: boolean;
  name: string;
  detail: string;
}) {
  return (
    <HStack spacing={3} align="center">
      <ColourSwatch hex={props.hex} reserveSlot={props.showSwatch} />
      <VStack spacing={0}>
        <Text size="small" alternative>
          {props.name}
        </Text>
        <Text size="xSmall" color="secondary">
          {props.detail}
        </Text>
      </VStack>
    </HStack>
  );
}

function ColourEditDialog({
  colour,
  trims,
  canViewCost,
  reload,
}: {
  colour: ColourResponseBody;
  trims: TrimResponseBody[];
  canViewCost: boolean;
  reload: () => void;
}) {
  const [update] = useUpdateColourMutation();
  const [putTrims] = usePutColourTrimsMutation();
  const refreshVersion = useRefreshPersistedColourVersion();
  const persisted = useRef<PersistedColour | null>(null);
  const publishedPrice = usePublishedPriceSave(colour.modelYearId);
  const defaultValues: ColourValues = {
    name: colour.name,
    kind: colour.kind ?? '',
    factoryCode: colour.factoryCode ?? '',
    hex: colour.hex ?? '',
    priceNet: colour.priceNet,
    costNet: colour.costNet ?? '',
    trimIds: colour.availableForTrimIds,
  };
  const save = async (values: ColourValues) => {
    const submittedValues = getColourScalarValues(values);
    if (
      !persisted.current ||
      JSON.stringify(persisted.current.values) !== JSON.stringify(submittedValues)
    ) {
      const previous = persisted.current;
      const updated = await update({
        colourId: previous?.id ?? colour.id,
        colourWriteRequestBody: {
          modelYearId: colour.modelYearId,
          name: values.name,
          codes: replaceFactoryCode(
            previous?.codes ?? colour.codes,
            previous?.values.factoryCode ?? colour.factoryCode,
            values.factoryCode
          ),
          kind: values.kind || null,
          hex: values.hex || null,
          factoryCode: values.factoryCode || null,
          priceNet: values.priceNet,
          costNet: canViewCost ? values.costNet || null : colour.costNet,
          version: previous?.version ?? colour.version,
        },
      }).unwrap();
      persisted.current = {
        id: updated.id,
        version: updated.version,
        codes: updated.codes,
        values: submittedValues,
      };
    }
    await putTrims({
      colourId: persisted.current.id,
      replaceColourTrimsRequestBody: {
        trimIds: values.trimIds,
        version: persisted.current.version,
      },
    }).unwrap();
  };
  const submit: FormSubmitHandler<ColourValues> = (values, setErrors) =>
    (async () => {
      await publishedPrice.save({
        values,
        storedValues: defaultValues,
        priceField: 'priceNet',
        isSavePending: persisted.current !== null,
        scope: 'colour',
        resourceId: colour.id,
        save,
        onDone: closeCurrentDialog,
      });
    })().catch(async (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (!isStaleCatalogueVersion(error)) {
        return;
      }
      reload();
      await refreshVersion(persisted);
    });
  return (
    <>
      <Form<ColourValues>
        experimentalZodSchema={colourSchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <ColourFields
            control={control}
            modelYearId={colour.modelYearId}
            trims={trims}
            canViewCost={canViewCost}
            submitTitle={t('general.actions.save', 'Save')}
          />
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}

function UpholsteryEditDialog({
  upholstery,
  canViewCost,
  reload,
}: {
  upholstery: UpholsteryResponseBody;
  canViewCost: boolean;
  reload: () => void;
}) {
  const [update] = useUpdateUpholsteryMutation();
  const publishedPrice = usePublishedPriceSave(upholstery.modelYearId);
  const defaultValues: UpholsteryValues = {
    name: upholstery.name,
    fabric: upholstery.fabric ?? '',
    factoryCode: upholstery.factoryCode ?? '',
    hex: upholstery.interiorColorHex ?? '',
    priceNet: upholstery.priceNet,
    costNet: upholstery.costNet ?? '',
  };
  const toUpdateRequest = (values: UpholsteryValues) => ({
    upholsteryId: upholstery.id,
    upholsteryWriteRequestBody: {
      modelYearId: upholstery.modelYearId,
      name: values.name,
      codes: replaceFactoryCode(upholstery.codes, upholstery.factoryCode, values.factoryCode),
      fabric: values.fabric || null,
      interiorColorHex: values.hex || null,
      factoryCode: values.factoryCode || null,
      priceNet: values.priceNet,
      costNet: canViewCost ? values.costNet || null : upholstery.costNet,
      version: upholstery.version,
    },
  });
  const submit: FormSubmitHandler<UpholsteryValues> = async (values, setErrors) => {
    await publishedPrice
      .save({
        values,
        storedValues: defaultValues,
        priceField: 'priceNet',
        scope: 'upholstery',
        resourceId: upholstery.id,
        save: (upholsteryValues) => update(toUpdateRequest(upholsteryValues)).unwrap(),
        onDone: closeCurrentDialog,
      })
      .catch((error: ApiError) => {
        handleCatalogueError(error, setErrors);
        if (isStaleCatalogueVersion(error)) {
          reload();
        }
      });
  };
  return (
    <>
      <Form<UpholsteryValues>
        experimentalZodSchema={upholsterySchema}
        defaultValues={defaultValues}
        onSubmit={submit}
      >
        {(control) => (
          <UpholsteryFields
            control={control}
            modelYearId={upholstery.modelYearId}
            canViewCost={canViewCost}
            submitTitle={t('general.actions.save', 'Save')}
          />
        )}
      </Form>
      <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
    </>
  );
}

export function Colours({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListColourQuery({});
  const trimsQuery = useListTrimQuery({});
  const [create] = useCreateColourMutation();
  const [update] = useUpdateColourMutation();
  const [archive] = useArchiveColourMutation();
  const [putTrims] = usePutColourTrimsMutation();
  const refreshVersion = useRefreshPersistedColourVersion();
  const publishedPrice = usePublishedPriceSave(modelYearId);
  const vatRate = useCatalogueVatRate(modelYearId);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const persisted = useRef<PersistedColour | null>(null);
  const rows =
    query.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ?? [];
  const showSwatch = hasAnySwatch(rows);
  const trims =
    trimsQuery.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ??
    [];
  const {getColorType} = useNewVehicleCatalogueLabels();
  const saveColour = async (values: ColourValues) => {
    const submittedValues = getColourScalarValues(values);
    if (!persisted.current) {
      const colour = await create({
        colourWriteRequestBody: {
          modelYearId,
          name: values.name,
          codes: values.factoryCode ? [values.factoryCode] : [],
          kind: values.kind || null,
          hex: values.hex || null,
          factoryCode: values.factoryCode || null,
          priceNet: values.priceNet,
          costNet: canViewCost ? values.costNet || null : undefined,
          version: 1,
        },
      }).unwrap();
      persisted.current = {
        id: colour.id,
        version: colour.version,
        codes: colour.codes,
        values: submittedValues,
      };
    } else if (JSON.stringify(persisted.current.values) !== JSON.stringify(submittedValues)) {
      const colour = await update({
        colourId: persisted.current.id,
        colourWriteRequestBody: {
          modelYearId,
          name: values.name,
          codes: replaceFactoryCode(
            persisted.current.codes,
            persisted.current.values.factoryCode,
            values.factoryCode
          ),
          kind: values.kind || null,
          hex: values.hex || null,
          factoryCode: values.factoryCode || null,
          priceNet: values.priceNet,
          costNet: canViewCost ? values.costNet || null : undefined,
          version: persisted.current.version,
        },
      }).unwrap();
      persisted.current = {
        id: colour.id,
        version: colour.version,
        codes: colour.codes,
        values: submittedValues,
      };
    }
    await putTrims({
      colourId: persisted.current.id,
      replaceColourTrimsRequestBody: {
        trimIds: values.trimIds,
        version: persisted.current.version,
      },
    }).unwrap();
  };
  const submit: FormSubmitHandler<ColourValues> = (values, setErrors) =>
    (async () => {
      const created = persisted.current;
      if (!created) {
        await saveColour(values);
        closeCreate();
        return;
      }
      // A retry after the trim assignment failed updates the created colour, so its price is stored.
      await publishedPrice.save({
        values,
        storedValues: {...values, ...created.values},
        priceField: 'priceNet',
        isSavePending: true,
        scope: 'colour',
        resourceId: created.id,
        save: saveColour,
        onDone: closeCreate,
      });
    })().catch(async (error: ApiError) => {
      handleCatalogueError(error, setErrors);
      if (isStaleCatalogueVersion(error)) {
        await refreshVersion(persisted);
      }
    });
  const openCreate = () => {
    persisted.current = null;
    setCreateOpen(true);
  };
  const closeCreate = () => {
    persisted.current = null;
    setCreateOpen(false);
  };
  const editColour = (row: ColourResponseBody) =>
    openDialog(
      <ColourEditDialog
        colour={row}
        trims={trims}
        canViewCost={canViewCost}
        reload={() => void query.refetch()}
      />,
      {
        title: t('entity.newVehicle.catalogue.editColourNamed', 'Edit colour · {name}', {
          name: row.name,
        }),
      }
    );
  const archiveColour = (row: ColourResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveColour', 'Archive colour {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archive({colourId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={3}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.colours',
          'Exterior paints customers can choose. A colour only appears in the configurator on the trims it is offered on.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.catalogue.tabs.colours', 'Colours')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addColour', 'Add colour'),
                  leftIcon: 'content/add',
                  onClick: openCreate,
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noColours', 'No colours yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.coloursHint',
            'A model year needs at least one colour before it can be published.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addColour', 'Add colour'),
                  leftIcon: 'content/add',
                  onClick: openCreate,
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.labels.colour', 'Colour')),
              column(t('entity.newVehicle.catalogue.offeredOn', 'Offered on'), {width: '30%'}),
              column(t('entity.newVehicle.catalogue.priceNet', 'Price net'), {
                width: '14%',
                align: 'right',
              }),
              ...(canViewCost
                ? [
                    column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                      width: '14%',
                      align: 'right',
                    }),
                  ]
                : []),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editColour(row) : undefined}>
                <Cell>
                  <SwatchName
                    hex={row.hex}
                    showSwatch={showSwatch}
                    name={row.name}
                    detail={joinParts([getColorType(row.kind), row.factoryCode])}
                  />
                </Cell>
                <Cell>
                  <OfferedOn trimIds={row.availableForTrimIds} trims={trims} />
                </Cell>
                <Cell align="right">
                  <PriceWithGross amount={row.priceNet} currency={currency} vatRate={vatRate} />
                </Cell>
                {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                {canViewCost ? (
                  <Cell align="right">
                    <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                  </Cell>
                ) : null}
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editColour(row)),
                        archiveRowAction(() => archiveColour(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addColour', 'Add colour')}
          onClose={closeCreate}
        >
          <Form<ColourValues>
            experimentalZodSchema={colourSchema}
            defaultValues={{
              name: '',
              kind: '',
              factoryCode: '',
              hex: '',
              priceNet: '0.00',
              costNet: '',
              trimIds: [],
            }}
            onSubmit={submit}
          >
            {(control) => (
              <ColourFields
                control={control}
                modelYearId={modelYearId}
                trims={trims}
                canViewCost={canViewCost}
                isCreate
                submitTitle={t('entity.newVehicle.catalogue.addColour', 'Add colour')}
              />
            )}
          </Form>
        </Dialog>
        <NewPricelistVersionDialog {...publishedPrice.dialogProps} />
      </Card>
    </VStack>
  );
}

const hasAnyUpholsterySwatch = (rows: UpholsteryResponseBody[]) =>
  hasAnySwatch(rows.map(({interiorColorHex}) => ({hex: interiorColorHex})));

// Both matrices put upholstery across the top; tiles keep a fixed width so the grid stays even.
const upholsteryColumns = (
  rows: UpholsteryResponseBody[],
  currency: string,
  firstLabel: string
) => {
  const showUpholsterySwatch = hasAnyUpholsterySwatch(rows);
  return [
    column(firstLabel, {width: 60}),
    ...rows.map((item) =>
      column(
        <SwatchLabel
          name={item.name}
          hex={item.interiorColorHex}
          showSwatch={showUpholsterySwatch}
          amount={item.priceNet}
          currency={currency}
          direction="column"
        />,
        {width: TOGGLE_COLUMN_WIDTH, align: 'center'}
      )
    ),
    // Filler so the fixed-width tiles hug the left instead of spreading across the card.
    column(''),
  ];
};

export function Upholsteries({
  modelYearId,
  currency,
  canWrite,
  canViewCost,
}: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
  canViewCost: boolean;
}) {
  const query = useListUpholsteryQuery({});
  const trimsQuery = useListTrimQuery({});
  const [create] = useCreateUpholsteryMutation();
  const [archive] = useArchiveUpholsteryMutation();
  const [putTrim] = usePutTrimUpholsteryMutation();
  const [isCreateOpen, setCreateOpen] = useState(false);
  const {enqueueSave, readTrimUpholstery} = useMatrixSaveQueue(modelYearId);
  const vatRate = useCatalogueVatRate(modelYearId);
  const ofModelYear = (items: UpholsteryResponseBody[] = []) =>
    items.filter((item) => item.modelYearId === modelYearId && !item.archived);
  const rows = ofModelYear(query.data?.items);
  const showSwatch = hasAnyUpholsterySwatch(rows);
  const currentIdsFor = (trimId: string, items = rows) =>
    items.filter(({availableForTrimIds}) => availableForTrimIds.includes(trimId)).map(({id}) => id);
  const trims =
    trimsQuery.data?.items.filter((item) => item.modelYearId === modelYearId && !item.archived) ??
    [];
  const {getInteriorMaterial} = useNewVehicleCatalogueLabels();
  const setTrimUpholstery = (trimId: string, upholsteryId: string, available: boolean) =>
    enqueueSave(async () => {
      const [trimList, upholsteryList] = await readTrimUpholstery();
      const trim = trimList.items.find(({id}) => id === trimId);
      if (isNil(trim)) {
        return;
      }
      const others = currentIdsFor(trimId, ofModelYear(upholsteryList.items)).filter(
        (id) => id !== upholsteryId
      );
      await putTrim({
        trimId,
        replaceTrimUpholsteryRequestBody: {
          upholsteryIds: available ? [...others, upholsteryId] : others,
          version: trim.version,
        },
      }).unwrap();
      await readTrimUpholstery();
    });
  const submit: FormSubmitHandler<UpholsteryValues> = (values, setErrors) =>
    create({
      upholsteryWriteRequestBody: {
        modelYearId,
        name: values.name,
        codes: values.factoryCode ? [values.factoryCode] : [],
        fabric: values.fabric || null,
        interiorColorHex: values.hex || null,
        factoryCode: values.factoryCode || null,
        priceNet: values.priceNet,
        costNet: canViewCost ? values.costNet || null : undefined,
        version: 1,
      },
    })
      .unwrap()
      .then(() => setCreateOpen(false))
      .catch((error: ApiError) => handleCatalogueError(error, setErrors));
  const editUpholstery = (row: UpholsteryResponseBody) =>
    openDialog(
      <UpholsteryEditDialog
        upholstery={row}
        canViewCost={canViewCost}
        reload={() => void query.refetch()}
      />,
      {
        title: t('entity.newVehicle.catalogue.editUpholsteryNamed', 'Edit upholstery · {name}', {
          name: row.name,
        }),
      }
    );
  const archiveUpholstery = (row: UpholsteryResponseBody) =>
    openConfirmDialog({
      text: t('entity.newVehicle.confirmations.archiveUpholstery', 'Archive upholstery {name}?', {
        name: row.name,
      }),
      onConfirm: () =>
        void archive({upholsteryId: row.id, versionedWriteRequestBody: {version: row.version}})
          .unwrap()
          .catch(handleCatalogueError),
    });
  return (
    <VStack spacing={4}>
      <TabIntro>
        {t(
          'entity.newVehicle.catalogue.intro.upholsteryTrims',
          'Interior trims customers can choose. Set which trims offer each one, then block any colour combinations the factory does not build on the Colour & upholstery tab.'
        )}
      </TabIntro>
      <Card
        title={t('entity.newVehicle.labels.upholstery', 'Upholstery')}
        actions={
          canWrite
            ? [
                {
                  type: 'button',
                  title: t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                },
              ]
            : []
        }
      >
        <DataStatus
          isLoading={query.isLoading}
          isError={query.isError}
          isEmpty={!rows.length}
          emptyMessage={t('entity.newVehicle.authoring.noUpholstery', 'No upholstery yet')}
          emptySubheadline={t(
            'entity.newVehicle.catalogue.empty.upholsteryHint',
            'A model year needs at least one upholstery before it can be published.'
          )}
          action={
            canWrite
              ? {
                  title: t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery'),
                  leftIcon: 'content/add',
                  onClick: () => setCreateOpen(true),
                }
              : undefined
          }
        >
          <Table
            columns={[
              column(t('entity.newVehicle.labels.upholstery', 'Upholstery')),
              column(t('entity.newVehicle.catalogue.offeredOn', 'Offered on'), {width: '30%'}),
              column(t('entity.newVehicle.catalogue.priceNet', 'Price net'), {
                width: '14%',
                align: 'right',
              }),
              ...(canViewCost
                ? [
                    column(t('entity.newVehicle.catalogue.costNet', 'Cost net'), {
                      width: '14%',
                      align: 'right',
                    }),
                  ]
                : []),
              actionsColumn(),
            ]}
          >
            {rows.map((row) => (
              <TableRow key={row.id} onRowClick={canWrite ? () => editUpholstery(row) : undefined}>
                <Cell>
                  <SwatchName
                    hex={row.interiorColorHex}
                    showSwatch={showSwatch}
                    name={row.name}
                    detail={joinParts([getInteriorMaterial(row.fabric), row.factoryCode])}
                  />
                </Cell>
                <Cell>
                  <OfferedOn trimIds={row.availableForTrimIds} trims={trims} />
                </Cell>
                <Cell align="right">
                  <PriceWithGross amount={row.priceNet} currency={currency} vatRate={vatRate} />
                </Cell>
                {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
                {canViewCost ? (
                  <Cell align="right">
                    <MoneyText amount={row.costNet ?? '0.00'} currency={currency} />
                  </Cell>
                ) : null}
                <Cell>
                  <Show when={canWrite}>
                    <RowActions
                      actions={[
                        editRowAction(() => editUpholstery(row)),
                        archiveRowAction(() => archiveUpholstery(row)),
                      ]}
                    />
                  </Show>
                </Cell>
              </TableRow>
            ))}
          </Table>
        </DataStatus>
        <Dialog
          isOpen={isCreateOpen}
          title={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
          onClose={() => setCreateOpen(false)}
        >
          <Form<UpholsteryValues>
            experimentalZodSchema={upholsterySchema}
            defaultValues={EMPTY_UPHOLSTERY}
            onSubmit={submit}
          >
            {(control) => (
              <UpholsteryFields
                control={control}
                modelYearId={modelYearId}
                canViewCost={canViewCost}
                isCreate
                submitTitle={t('entity.newVehicle.catalogue.addUpholstery', 'Add upholstery')}
              />
            )}
          </Form>
        </Dialog>
      </Card>
      <Show when={rows.length > 0 && trims.length > 0}>
        <Card
          title={t(
            'entity.newVehicle.catalogue.trimUpholsteryQuestion',
            'Which upholstery comes with each trim?'
          )}
          subtitle={t(
            'entity.newVehicle.catalogue.trimUpholsteryHint',
            'Click a cell to choose which upholstery a customer can pick on each trim.'
          )}
        >
          <Table
            columns={upholsteryColumns(
              rows,
              currency,
              t('entity.newVehicle.catalogue.trimLevel', 'Trim level')
            )}
          >
            {trims.map((trim) => (
              <TableRow key={trim.id}>
                <Cell>
                  <VStack spacing={0}>
                    <Text size="small" alternative>
                      {trim.name}
                    </Text>
                    <Show when={!currentIdsFor(trim.id).length}>
                      <Text size="xSmall" color="warning">
                        {t(
                          'entity.newVehicle.catalogue.trimWithoutUpholstery',
                          'No upholstery selected'
                        )}
                      </Text>
                    </Show>
                  </VStack>
                </Cell>
                {rows.map((item) => {
                  const checked = currentIdsFor(trim.id).includes(item.id);
                  return (
                    <Cell key={item.id} align="center">
                      <ToggleTile
                        isDisabled={!canWrite}
                        isOn={checked}
                        aria-label={`${trim.name}, ${item.name}`}
                        onToggle={() => setTrimUpholstery(trim.id, item.id, !checked)}
                      />
                    </Cell>
                  );
                })}
                <Cell>{null}</Cell>
              </TableRow>
            ))}
          </Table>
        </Card>
      </Show>
    </VStack>
  );
}

export function ColourUpholsteryCombinations(props: {
  modelYearId: string;
  currency: string;
  canWrite: boolean;
}) {
  const query = useListUpholsteryQuery({});
  const coloursQuery = useListColourQuery({});
  const restrictionsQuery = useGetColourUpholsteryRestrictionsQuery({
    modelYearId: props.modelYearId,
  });
  const [putRestrictions] = usePutColourUpholsteryRestrictionsMutation();
  const {enqueueSave, readRestrictions} = useMatrixSaveQueue(props.modelYearId);
  const rows =
    query.data?.items.filter((item) => item.modelYearId === props.modelYearId && !item.archived) ??
    [];
  const colours =
    coloursQuery.data?.items.filter(
      (item) => item.modelYearId === props.modelYearId && !item.archived
    ) ?? [];
  const restrictions = restrictionsQuery.data?.restrictions ?? [];
  const isRestricted = (colourId: string, upholsteryId: string) =>
    restrictions.some((pair) => pair.colourId === colourId && pair.upholsteryId === upholsteryId);
  const setRestricted = (colourId: string, upholsteryId: string, restricted: boolean) =>
    enqueueSave(async () => {
      const latest = await readRestrictions();
      const others = latest.restrictions.filter(
        (pair) => pair.colourId !== colourId || pair.upholsteryId !== upholsteryId
      );
      await putRestrictions({
        modelYearId: props.modelYearId,
        replaceColourUpholsteryRestrictionsRequestBody: {
          version: latest.version,
          restrictions: restricted ? [...others, {colourId, upholsteryId}] : others,
        },
      }).unwrap();
      await readRestrictions();
    });
  const showColourSwatch = hasAnySwatch(colours);
  return (
    <Card
      title={t(
        'entity.newVehicle.catalogue.colourUpholsteryQuestion',
        'Which colour and upholstery combinations can be ordered?'
      )}
      subtitle={t(
        'entity.newVehicle.catalogue.colourUpholsteryHint',
        'All combinations are allowed by default. Click a cell to block one the factory does not build.'
      )}
    >
      <DataStatus
        isLoading={query.isLoading || coloursQuery.isLoading || restrictionsQuery.isLoading}
        isError={query.isError || coloursQuery.isError || restrictionsQuery.isError}
        isEmpty={!rows.length || !colours.length}
        emptyMessage={t(
          'entity.newVehicle.catalogue.colourUpholsteryEmpty',
          'Add colours and upholstery first'
        )}
      >
        <Table
          columns={upholsteryColumns(
            rows,
            props.currency,
            t('entity.newVehicle.labels.colour', 'Colour')
          )}
        >
          {colours.map((colour) => (
            <TableRow key={colour.id}>
              <Cell>
                <SwatchLabel
                  name={colour.name}
                  hex={colour.hex}
                  showSwatch={showColourSwatch}
                  amount={colour.priceNet}
                  currency={props.currency}
                  direction="row"
                />
              </Cell>
              {rows.map((item) => (
                <Cell key={item.id} align="center">
                  <ToggleTile
                    isDisabled={!props.canWrite || !restrictionsQuery.data}
                    isOn={!isRestricted(colour.id, item.id)}
                    aria-label={`${colour.name}, ${item.name}`}
                    onToggle={() =>
                      setRestricted(colour.id, item.id, !isRestricted(colour.id, item.id))
                    }
                  />
                </Cell>
              ))}
              <Cell>{null}</Cell>
            </TableRow>
          ))}
        </Table>
      </DataStatus>
    </Card>
  );
}
